/**
 * LuvTune - Custom Music Store JavaScript
 * Audio Player, Form Validation, and Interactive Features
 */

(function() {
  'use strict';

  // ==========================================
  // Audio Player Class
  // ==========================================
  class LuvTunePlayer {
    constructor(element) {
      this.container = element;
      this.audio = element.querySelector('[data-audio]');
      this.playBtn = element.querySelector('[data-play-btn]');
      this.progressWrapper = element.querySelector('[data-progress-wrapper]');
      this.progressBar = element.querySelector('[data-progress-bar]');
      this.currentTimeEl = element.querySelector('[data-current-time]');
      this.durationEl = element.querySelector('[data-duration]');
      this.volumeBtn = element.querySelector('[data-volume-btn]');

      if (!this.audio) return;

      this.isPlaying = false;
      this.isMuted = false;

      this.bindEvents();
    }

    bindEvents() {
      // Play/Pause
      if (this.playBtn) {
        this.playBtn.addEventListener('click', () => this.togglePlay());
      }

      // Audio events
      this.audio.addEventListener('loadedmetadata', () => this.updateDuration());
      this.audio.addEventListener('timeupdate', () => this.updateProgress());
      this.audio.addEventListener('ended', () => this.onEnded());
      this.audio.addEventListener('play', () => this.onPlay());
      this.audio.addEventListener('pause', () => this.onPause());

      // Progress bar click
      if (this.progressWrapper) {
        this.progressWrapper.addEventListener('click', (e) => this.seek(e));
      }

      // Volume toggle
      if (this.volumeBtn) {
        this.volumeBtn.addEventListener('click', () => this.toggleMute());
      }
    }

    togglePlay() {
      if (this.isPlaying) {
        this.audio.pause();
      } else {
        // Pause all other players first
        document.querySelectorAll('[data-luvtune-player]').forEach(player => {
          const audio = player.querySelector('[data-audio]');
          if (audio && audio !== this.audio) {
            audio.pause();
          }
        });
        this.audio.play();
      }
    }

    onPlay() {
      this.isPlaying = true;
      this.container.classList.add('is-playing');
    }

    onPause() {
      this.isPlaying = false;
      this.container.classList.remove('is-playing');
    }

    onEnded() {
      this.isPlaying = false;
      this.container.classList.remove('is-playing');
      if (this.progressBar) {
        this.progressBar.style.width = '0%';
      }
    }

    updateDuration() {
      if (this.durationEl && this.audio.duration) {
        this.durationEl.textContent = this.formatTime(this.audio.duration);
      }
    }

    updateProgress() {
      if (!this.audio.duration) return;

      const progress = (this.audio.currentTime / this.audio.duration) * 100;

      if (this.progressBar) {
        this.progressBar.style.width = progress + '%';
      }

      if (this.currentTimeEl) {
        this.currentTimeEl.textContent = this.formatTime(this.audio.currentTime);
      }
    }

    seek(event) {
      if (!this.audio.duration) return;

      const rect = this.progressWrapper.getBoundingClientRect();
      const percent = (event.clientX - rect.left) / rect.width;
      this.audio.currentTime = percent * this.audio.duration;
    }

    toggleMute() {
      this.isMuted = !this.isMuted;
      this.audio.muted = this.isMuted;
      this.container.classList.toggle('is-muted', this.isMuted);
    }

    formatTime(seconds) {
      if (isNaN(seconds)) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return mins + ':' + (secs < 10 ? '0' : '') + secs;
    }
  }

  // ==========================================
  // Form Validation
  // ==========================================
  class LuvTuneForm {
    constructor(formElement) {
      this.form = formElement;
      if (!this.form) return;

      this.requiredFields = this.form.querySelectorAll('[required]');
      this.textareas = this.form.querySelectorAll('.luvtune-textarea');
      this.dateInput = this.form.querySelector('#luvtune-date');

      this.init();
    }

    init() {
      this.setupDateMin();
      this.setupCharCounters();
      this.setupValidation();
    }

    setupDateMin() {
      if (!this.dateInput) return;

      const today = new Date();
      const minDate = new Date(today);
      minDate.setDate(minDate.getDate() + 1); // 1 day from now

      this.dateInput.min = minDate.toISOString().split('T')[0];
    }

    setupCharCounters() {
      this.textareas.forEach(textarea => {
        const counterId = textarea.id;
        const counter = document.querySelector('.luvtune-char-count[data-for="' + counterId + '"]');

        if (counter) {
          // Update on input
          textarea.addEventListener('input', function() {
            counter.textContent = this.value.length;
          });

          // Initial count
          counter.textContent = textarea.value.length;
        }
      });
    }

    setupValidation() {
      this.requiredFields.forEach(field => {
        field.addEventListener('blur', () => this.validateField(field));
        field.addEventListener('input', () => this.clearError(field));
      });
    }

    validateField(field) {
      if (!field.value.trim()) {
        this.showError(field, 'This field is required');
        return false;
      }

      if (field.type === 'date') {
        const selectedDate = new Date(field.value);
        const minDate = new Date();
        minDate.setDate(minDate.getDate() + 1);

        if (selectedDate < minDate) {
          this.showError(field, 'Please select a date at least 1 day from today');
          return false;
        }
      }

      this.clearError(field);
      return true;
    }

    showError(field, message) {
      this.clearError(field);
      field.classList.add('luvtune-input--error');

      const errorEl = document.createElement('span');
      errorEl.className = 'luvtune-form__error';
      errorEl.textContent = message;
      errorEl.style.cssText = 'color: #e74c3c; font-size: 0.85rem; display: block; margin-top: 0.25rem;';

      field.parentNode.appendChild(errorEl);
    }

    clearError(field) {
      field.classList.remove('luvtune-input--error');
      const existingError = field.parentNode.querySelector('.luvtune-form__error');
      if (existingError) {
        existingError.remove();
      }
    }

    validateAll() {
      let isValid = true;
      this.requiredFields.forEach(field => {
        if (!this.validateField(field)) {
          isValid = false;
        }
      });
      return isValid;
    }
  }

  // ==========================================
  // Initialize on DOM Ready
  // ==========================================
  function init() {
    // Initialize all audio players
    document.querySelectorAll('[data-luvtune-player]').forEach(player => {
      new LuvTunePlayer(player);
    });

    // Initialize form validation
    const songForm = document.getElementById('song-customization-form');
    if (songForm) {
      window.luvtuneForm = new LuvTuneForm(songForm);
    }

    // Form submission validation
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm && songForm) {
      productForm.addEventListener('submit', function(e) {
        if (window.luvtuneForm && !window.luvtuneForm.validateAll()) {
          e.preventDefault();
          // Scroll to first error
          const firstError = songForm.querySelector('.luvtune-input--error');
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstError.focus();
          }
        }
      });
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize after Shopify section renders (for theme editor)
  document.addEventListener('shopify:section:load', init);

  // Expose to global scope for debugging
  window.LuvTunePlayer = LuvTunePlayer;
  window.LuvTuneForm = LuvTuneForm;

})();
